import { Cpu, TestTube, Briefcase } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';

export const Learning = () => {
  const learningAreas = [
    {
      id: 'chip-design',
      icon: Cpu,
      title: "Chip Design Basics",
      description: "Master the fundamentals of digital circuit design, VLSI architecture, and semiconductor physics. Learn industry-standard design flows from RTL to layout.",
      skills: [
        { id: 'digital-logic', name: "Digital Logic" },
        { id: 'vlsi', name: "VLSI Architecture" },
        { id: 'design-flows', name: "Design Flows" },
        { id: 'semiconductor', name: "Semiconductor Physics" }
      ]
    },
    {
      id: 'verification',
      icon: TestTube,
      title: "Verification Techniques",
      description: "Develop expertise in verification methodologies including simulation, formal verification, and coverage analysis to ensure chip functionality.",
      skills: [
        { id: 'simulation', name: "Simulation" },
        { id: 'formal-verification', name: "Formal Verification" },
        { id: 'coverage', name: "Coverage Analysis" },
        { id: 'validation', name: "Design Validation" }
      ]
    },
    {
      id: 'industry-skills',
      icon: Briefcase,
      title: "Industry-Relevant Skills",
      description: "Build professional competencies that employers seek, including teamwork, project management, and exposure to cutting-edge EDA tools.",
      skills: [
        { id: 'eda-tools', name: "EDA Tools" },
        { id: 'project-mgmt', name: "Project Management" },
        { id: 'networking', name: "Industry Networking" },
        { id: 'professional-dev', name: "Professional Development" }
      ]
    }
  ];

  return (
    <section id="learning" className="py-24 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            What You'll Learn
          </h2>
          <div className="w-24 h-1 bg-[#BB0000] mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive training that prepares you for a successful career in semiconductor design
          </p>
        </div>

        {/* Collapsible Learning Areas */}
        <Accordion type="single" collapsible className="space-y-4">
          {learningAreas.map((area) => (
            <AccordionItem 
              key={area.id} 
              value={area.id}
              className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:border-[#BB0000] transition-colors"
            >
              <AccordionTrigger className="px-6 py-4 hover:no-underline">
                <div className="flex items-center gap-4 text-left w-full">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#BB0000]/10 rounded-lg flex items-center justify-center">
                    <area.icon className="text-[#BB0000]" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900">{area.title}</h3>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <p className="text-gray-600 leading-relaxed mb-4 ml-16">
                  {area.description}
                </p>
                <div className="ml-16">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
                    Key Skills
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {area.skills.map((skill) => (
                      <span 
                        key={skill.id}
                        className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full border border-gray-200"
                      >
                        {skill.name}
                      </span>
                    ))}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
